import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useQueryState } from "nuqs";
import {
  fetchCampaigns,
  deleteCampaign,
  fetchAllAgents,
  Campaign,
  CampaignStatus,
} from "../../services/api";
import {
  HiOutlineTrash, 
  HiOutlineEye,
  HiOutlineSearch,
  HiOutlineFilter,
  HiPlus,
} from "react-icons/hi";
import LoadingState from "../Common/LoadingState";
import ConfirmationModal from "../Common/ConfirmationModal";
import CreateCampaign from "../Admin/CreateCampaign";
import { Modal } from "../Common/Modal";
import { useNavigate } from "react-router-dom";

const LeadCampaigns: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // URL state management with nuqs
  const [currentPage, setCurrentPage] = useQueryState("page", { defaultValue: 1, parse: (value) => parseInt(value) || 1 });
  const [searchQuery, setSearchQuery] = useQueryState("search", { defaultValue: "" });
  const [statusFilter, setStatusFilter] = useQueryState("status", { defaultValue: "" });

  // Local state for debounced search input
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>(searchQuery);

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [campaignToDelete, setCampaignToDelete] = useState<string>("");

  // Debounce search input to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to first page when search or status changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, statusFilter, setCurrentPage]);

  // Use React Query for campaigns
  const { data: campaignsData, isLoading: campaignsLoading, error: campaignsError } = useQuery({
    queryKey: ['leadCampaigns', currentPage, statusFilter, debouncedSearchQuery],
    queryFn: async () => {
      const result = await fetchCampaigns(currentPage, 10, (statusFilter as CampaignStatus) || undefined, debouncedSearchQuery || undefined);
      return result;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Use React Query for agents
  const { data: agentsData, refetch: refetchAgents } = useQuery({
    queryKey: ['leadAgents'],
    queryFn: async () => {
      const response = await fetchAllAgents(50, 0);
      // Filter to only show agents accessible to this lead
      return response.data?.filter(agent => !agent.is_public && agent.enabled) || [];
    },
  });

  const campaigns = campaignsData?.campaigns || [];
  const totalPages = Math.ceil((campaignsData?.total || 0) / (campaignsData?.page_size || 10));
  const agents = agentsData || [];

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleDelete = async (campaignId: string) => {
    try {
      const result = await deleteCampaign(campaignId);
      if (result.success) {
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['leadCampaigns'] });
        setShowDeleteModal(false);
      } else {
        console.error(result.error || "Failed to delete campaign");
      }
    } catch (err: any) {
      console.error(`Failed to delete campaign: ${err.message}`);
    }
  };

  const openDeleteModal = (campaignId: string) => {
    setCampaignToDelete(campaignId);
    setShowDeleteModal(true);
  };

  const getStatusBadge = (status: CampaignStatus) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case CampaignStatus.DRAFT:
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case CampaignStatus.RUNNING:
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case CampaignStatus.PAUSED:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case CampaignStatus.COMPLETED:
        return `${baseClasses} bg-green-100 text-green-800`;
      case CampaignStatus.FAILED:
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getSuccessRate = (campaign: Campaign) => {
    if (campaign.processed_contacts === 0) return 0;
    return Math.round((campaign.successful_calls / campaign.processed_contacts) * 100);
  };

  // Remove frontend filtering since we're now filtering on the backend
  const filteredCampaigns = campaigns;

  if (campaignsError) {
    return (
      <div className="p-6">
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">Failed to load campaigns</p>
        </div>
      </div>
    );
  }

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    // Invalidate campaigns query to refresh the list
    queryClient.invalidateQueries({ queryKey: ['leadCampaigns'] });
  };

  const handleOpenCreateModal = () => {
    // Refetch agents to ensure fresh data when opening create campaign modal
    refetchAgents();
    setShowCreateModal(true);
  };

  return (
    <div className="p-6">
      {campaignsLoading && campaigns.length === 0 && <LoadingState message="Loading your campaigns..." />}
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--foreground)]">My Campaigns</h1>
          <p className="text-[var(--foreground-muted)]">Create and manage your AI voice agent campaigns</p>
        </div>
        <button
          onClick={handleOpenCreateModal}
          className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <HiPlus className="h-5 w-5" />
          Create Campaign
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search campaigns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
          />
        </div>
        <div className="flex items-center gap-2">
          <HiOutlineFilter className="text-gray-400 h-5 w-5" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as CampaignStatus | "")}
            className="border border-[var(--border)] rounded-lg px-3 py-2 focus:ring-2 focus:ring-accent focus:border-transparent text-[var(--foreground)] bg-[var(--primary-foreground)]"
          >
            <option value="">All Status</option>
            <option value={CampaignStatus.DRAFT}>Draft</option>
            <option value={CampaignStatus.RUNNING}>Running</option>
            <option value={CampaignStatus.PAUSED}>Paused</option>
            <option value={CampaignStatus.COMPLETED}>Completed</option>
            <option value={CampaignStatus.FAILED}>Failed</option>
          </select>
        </div>
      </div>

      {/* Campaigns Table */}
      <div className="bg-[var(--card)] rounded-lg shadow-sm border border-[var(--border)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-[var(--muted)]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--foreground-muted)] uppercase tracking-wider">
                  Campaign Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--foreground-muted)] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--foreground-muted)] uppercase tracking-wider">
                  Agent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--foreground-muted)] uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-[var(--foreground-muted)] uppercase tracking-wider">
                  Total Contacts
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--foreground-muted)] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[var(--card)] divide-y divide-[var(--border)]">
              {filteredCampaigns.map((campaign) => (
                <tr 
                  key={campaign.id}
                  onClick={() => navigate(`/campaigns/${campaign.id}`)}
                  className="cursor-pointer hover:bg-[var(--muted)] transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-[var(--foreground)]">{campaign.name}</div>
                      <div className="text-sm text-[var(--foreground)]">
                        Created {new Date(campaign.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getStatusBadge(campaign.status)}>
                      {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-[var(--foreground)]">
                      {campaign.agent_name || "Unknown Agent"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-[var(--foreground)]">
                      {campaign.processed_contacts} / {campaign.total_contacts}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                      <div
                        className="bg-accent h-2 rounded-full"
                        style={{
                          width: `${campaign.total_contacts > 0 ? (campaign.processed_contacts / campaign.total_contacts) * 100 : 0}%`,
                        }}
                      ></div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-center text-[var(--foreground)]">{campaign.total_contacts}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/campaigns/${campaign.id}`);
                        }}
                        className="text-primary hover:text-primary/80"
                        title="View Details"
                      >
                        <HiOutlineEye className="h-5 w-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteModal(campaign.id);
                        }}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Campaign"
                      >
                        <HiOutlineTrash className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredCampaigns.length === 0 && !campaignsLoading && (
          <div className="text-center py-12">
            <div className="text-[var(--foreground-muted)]">
              {debouncedSearchQuery || statusFilter ? "No campaigns match your filters" : "No campaigns created yet"}
            </div>
            {!debouncedSearchQuery && !statusFilter && (
              <button
                onClick={handleOpenCreateModal}
                className="mt-4 text-primary hover:text-primary/80 font-medium"
              >
                Create your first campaign
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 bg-[var(--card)] border border-[var(--border)] rounded-lg mt-4">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-[var(--border)] text-sm font-medium rounded-md text-[var(--foreground)] bg-[var(--card)] hover:bg-[var(--muted)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-[var(--border)] text-sm font-medium rounded-md text-[var(--foreground)] bg-[var(--card)] hover:bg-[var(--muted)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing page <span className="font-medium">{currentPage}</span> of{" "}
                <span className="font-medium">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-[var(--border)] bg-[var(--card)] text-sm font-medium text-[var(--foreground-muted)] hover:bg-[var(--muted)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => handlePageChange(i + 1)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === i + 1
                        ? "z-10 bg-accent border-accent text-white"
                        : "bg-[var(--card)] border-[var(--border)] text-[var(--foreground-muted)] hover:bg-[var(--muted)]"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-[var(--border)] bg-[var(--card)] text-sm font-medium text-[var(--foreground-muted)] hover:bg-[var(--muted)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Create New Campaign"
        >
          <CreateCampaign
            agents={agents}
            onSuccess={handleCreateSuccess}
            onCancel={() => setShowCreateModal(false)}
          />
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => handleDelete(campaignToDelete)}
        title="Delete Campaign"
        message="Are you sure you want to delete this campaign? This action cannot be undone and will remove all associated data."
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive
      />
    </div>
  );
};

export default LeadCampaigns; 